/**
 * Legal form upgrade — pure helper extracted from coreSlice.upgradeLegalForm
 * (11/02/2026 — refactor coreSlice).
 *
 * Validates the requested upgrade, computes the capital top-up cost, and
 * returns the next slice patch + an optional notifier message. The actual
 * `set()` and `pushCustomMessage` calls stay in the slice so this helper
 * remains a pure function.
 *
 * Business rules :
 *   • Only allow upgrades along the order : auto → ei → eurl → sarl → sas.
 *   • The capital top-up (Δ = max(0, targetCapital - currentCapital)) is
 *     consigned immediately (debited from treasury) as engagement proof.
 *   • Effective bascule deferred to the 1st of the next month (réalisme
 *     procédural : notaire + greffe + K-bis ≈ 15-30 j).
 *
 * Returns one of three result shapes :
 *   { ok: false, reason: 'no_company' | 'unknown_target' | 'downgrade' }
 *   { ok: false, reason: 'insufficient_capital', message }
 *   { ok: true, patch, message }
 *
 * @param {Object} state - Current store state.
 * @param {string} targetForm - 'auto' | 'ei' | 'eurl' | 'sarl' | 'sas'.
 * @param {Object} deps
 * @param {Object} deps.LEGAL_FORMS - LEGAL_FORMS dictionary.
 * @param {Function} deps.getDefaultFiscalRegime - (form) → 'IR' | 'IS'.
 */
export function buildLegalFormUpgrade(state, targetForm, deps) {
  const { LEGAL_FORMS, getDefaultFiscalRegime } = deps;
  if (!state.company) return { ok: false, reason: 'no_company' };
  const current = state.company.legalForm || 'ei';
  const targetDef = LEGAL_FORMS[targetForm];
  if (!targetDef) return { ok: false, reason: 'unknown_target' };

  const order = ['auto', 'ei', 'eurl', 'sarl', 'sas'];
  if (order.indexOf(targetForm) <= order.indexOf(current)) {
    return { ok: false, reason: 'downgrade' };
  }

  const currentCapital = state.company.capitalSocial || 0;
  const targetCapital = targetDef.capitalRecommended || targetDef.capitalMin || 0;
  const capitalDelta = Math.max(0, targetCapital - currentCapital);

  if (state.treasury < capitalDelta) {
    return {
      ok: false,
      reason: 'insufficient_capital',
      message: {
        senderType: 'bank',
        senderName: 'Centre des Formalités',
        tone: 'urgent',
        kind: 'warning',
        subject: `Capital insuffisant pour passer en ${targetDef.shortName}`,
        body: `Le passage en ${targetDef.fullLabel} requiert un capital social de ${targetCapital.toLocaleString('fr-FR')} €. Vous disposez actuellement de ${currentCapital.toLocaleString('fr-FR')} €. Il manque ${capitalDelta.toLocaleString('fr-FR')} €. Reconstituez votre trésorerie avant de retenter.`,
        ttlDays: 14,
      },
    };
  }

  const targetRegime = getDefaultFiscalRegime(targetForm);
  // 02/06/2026 — Effet différé au 1er du mois suivant (réalisme procédural).
  const day = state.day || 1;
  const month = state.month || 1;
  const year = state.year || 2026;
  const plannedMonth = month === 12 ? 1 : month + 1;
  const plannedYear = month === 12 ? year + 1 : year;
  const plannedTotalDays = (state.totalDays || 0) + (30 - day + 1);

  const patch = {
    treasury: state.treasury - capitalDelta,
    company: {
      ...state.company,
      capitalSocial: Math.max(currentCapital, targetCapital),
      pendingLegalFormChange: {
        targetForm,
        targetRegime,
        plannedDay: 1,
        plannedMonth,
        plannedYear,
        plannedTotalDays,
        reason: 'Évolution manuelle programmée',
        manual: true,
      },
    },
  };

  const message = {
    senderType: 'system',
    senderName: 'Centre des Formalités',
    tone: 'friendly',
    kind: 'info',
    subject: `Dossier déposé : passage en ${targetDef.shortName} au 01/${String(plannedMonth).padStart(2, '0')}/${plannedYear}`,
    body: `Votre dossier de transformation en ${targetDef.fullLabel} a été déposé au greffe. Le capital de ${targetCapital.toLocaleString('fr-FR')} € est consigné (prélèvement immédiat de ${capitalDelta.toLocaleString('fr-FR')} €). La bascule effective interviendra au 01/${String(plannedMonth).padStart(2, '0')}/${plannedYear} (délai administratif moyen).`,
    ttlDays: 30,
  };

  return { ok: true, patch, message };
}
